// Keep Bootstrap's modal lifecycle intact: docking changes presentation only,
// so Vue, streaming requests and the Daily call retain their existing state.
export function installChatWindows() {
  const windows = ['chatModal', 'audioCallModal']
    .map(id => document.getElementById(id)).filter(Boolean);
  if (!windows.length || !window.bootstrap) return;

  const desktop = window.matchMedia('(min-width: 992px) and (hover: hover) and (pointer: fine)');
  const narrowDesktop = window.matchMedia('(max-width: 1399.98px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const openModals = new Set();
  const animations = new Map();
  let modalToggleTarget = null;
  let usedDock = false;
  let restoreNavigation = false;

  const instanceFor = modal => bootstrap.Modal.getInstance(modal);
  const isDocked = modal => modal.classList.contains('chat-window-docked');

  // Keep a visible passage below the sticky header as the text reflows.
  function captureReadingPosition() {
    const reader = document.getElementById('right-side-container');
    const content = reader?.querySelector('.document-container');
    if (!content || reader.scrollTop <= 0) return () => {};
    const readingTop = () => reader.getBoundingClientRect().top
      + (reader.querySelector('.body-header')?.offsetHeight || 0);
    const top = readingTop();
    const bottom = reader.getBoundingClientRect().bottom;
    let anchor;
    for (const element of content.querySelectorAll('p, li, h1, h2, h3, h4, h5, h6, pre, tr, img')) {
      const rect = element.getBoundingClientRect();
      if (rect.height && rect.bottom > top && rect.top < bottom) {
        anchor ||= element;
        if (rect.top >= top) {
          anchor = element;
          break;
        }
      }
    }
    if (!anchor) return () => {};
    const offset = anchor.getBoundingClientRect().top - top;
    return () => {
      if (anchor.isConnected) {
        reader.scrollTop += anchor.getBoundingClientRect().top - readingTop() - offset;
      }
    };
  }

  function setNavigationCollapsed(collapsed) {
    document.querySelector('.left-side-menu')?.classList.toggle('width-0', collapsed);
    document.querySelector('.right-side-container')?.classList.toggle('width-100', collapsed);
    document.querySelector('.menu-collapse-button')?.classList.toggle('rotate', collapsed);
    document.body.classList.toggle('side-bar-collapsed', collapsed);
  }

  function updateReadingLayout() {
    const needsSpace = desktop.matches && narrowDesktop.matches && windows.some(isDocked);
    const sidebar = document.querySelector('.left-side-menu');
    if (needsSpace && sidebar && !sidebar.classList.contains('width-0')) {
      restoreNavigation = true;
      setNavigationCollapsed(true);
    } else if (!needsSpace && restoreNavigation) {
      setNavigationCollapsed(false);
      restoreNavigation = false;
    }
  }

  const blockingModals = () => [...openModals].filter(modal => {
    if (isDocked(modal)) return false;
    const instance = instanceFor(modal);
    // Some existing application fallbacks hide dialogs directly, without a
    // Bootstrap hidden event. Do not let those stale entries lock the page.
    return modal.classList.contains('show')
      || (instance?._isShown && instance?._isTransitioning && modal.style.display !== 'none');
  });

  function updateControls(modal) {
    const restoreReadingPosition = captureReadingPosition();
    document.body.classList.add('chat-reading-resizing');
    const docked = isDocked(modal);
    modal.querySelectorAll('[data-chat-window-toggle]').forEach(button => {
      const label = docked ? 'Ampliar ventana' : 'Minimizar ventana';
      button.setAttribute('aria-label', label);
      button.setAttribute('title', label);
      button.setAttribute('aria-expanded', String(!docked));
    });
    document.body.classList.toggle('has-docked-chat', windows.some(isDocked));
    updateReadingLayout();
    restoreReadingPosition();
    // Flush layout before restoring the sidebar's ordinary toggle animation.
    document.getElementById('right-side-container')?.getBoundingClientRect();
    document.body.classList.remove('chat-reading-resizing');
  }

  function updateStacking() {
    const top = blockingModals().at(-1);
    windows.forEach(modal => {
      const wasBackground = modal.classList.contains('chat-window-background');
      const background = modal.classList.contains('show') && !!top && top !== modal;
      modal.classList.toggle('chat-window-background', background);
      if (background) {
        modal.setAttribute('inert', '');
        modal.setAttribute('aria-hidden', 'true');
      } else if (wasBackground) {
        modal.removeAttribute('inert');
        if (modal.classList.contains('show')) modal.removeAttribute('aria-hidden');
      }
    });
  }

  // Bootstrap 5.2 has no public API for suspending a shown modal's focus trap,
  // backdrop or scroll lock. Keep that integration here and never dispose or
  // recreate its instance (other application handlers hold references to it).
  function releasePage(modal) {
    if (blockingModals().length) return;
    instanceFor(modal)?._scrollBar.reset();
    document.body.classList.remove('modal-open');
    document.querySelector('.body-header')?.style.setProperty('position', 'sticky', 'important');
  }

  function cancelAnimation(modal) {
    animations.get(modal)?.cancel();
    animations.delete(modal);
    modal.classList.remove('chat-window-animating');
  }

  function setDocked(modal, docked, animate = true) {
    const instance = instanceFor(modal);
    if (!instance || instance._isTransitioning || !modal.classList.contains('show')) return;
    if (docked && !desktop.matches) return;
    if (isDocked(modal) === docked) return;

    usedDock = true;
    const dialog = modal.querySelector('.modal-dialog');
    const before = dialog.getBoundingClientRect();
    cancelAnimation(modal);
    modal.dispatchEvent(new CustomEvent('chat-window-will-resize'));
    modal.classList.toggle('chat-window-docked', docked);
    instance._backdrop._element?.classList.toggle('chat-window-backdrop', docked);
    if (docked) {
      instance._focustrap.deactivate();
      modal.removeAttribute('aria-modal');
      releasePage(modal);
    } else {
      modal.setAttribute('aria-modal', 'true');
      // Another modal may already own the page's scroll lock.
      if (!document.body.classList.contains('modal-open')) instance._scrollBar.hide();
      document.body.classList.add('modal-open');
      document.querySelector('.body-header')?.style.setProperty('position', 'static', 'important');
      if (blockingModals().at(-1) === modal) instance._focustrap.activate();
    }
    updateControls(modal);
    updateStacking();
    modal.dispatchEvent(new CustomEvent('chat-window-resize', { detail: { docked } }));

    // FLIP animates only a transform, avoiding per-frame layout of the entire
    // conversation. The actual layout and scroll containers resize once.
    if (animate && !reducedMotion.matches && dialog.animate) {
      const after = dialog.getBoundingClientRect();
      modal.classList.add('chat-window-animating');
      const animation = dialog.animate([
        { transformOrigin: 'top left', transform: `translate(${before.left - after.left}px, ${before.top - after.top}px) scale(${before.width / after.width}, ${before.height / after.height})` },
        { transformOrigin: 'top left', transform: 'none' },
      ], { duration: 260, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' });
      animations.set(modal, animation);
      animation.onfinish = () => cancelAnimation(modal);
    }
  }

  document.addEventListener('click', event => {
    // A deliberate navigation choice takes precedence over automatic restoration.
    if (event.target.closest('.menu-collapse-button')) restoreNavigation = false;
    const button = event.target.closest('[data-chat-window-toggle]');
    if (!button) return;
    const modal = button.closest('.modal');
    if (!windows.includes(modal)) return;
    event.preventDefault();
    setDocked(modal, !isDocked(modal));
  });

  // Bootstrap's delegated data API tries to hide the first .modal.show before
  // opening any other modal. Preserve the dock when navigating the workspace.
  // Capture on window runs before Bootstrap's document capture listener.
  window.addEventListener('click', event => {
    const toggle = event.target.closest('[data-bs-toggle="modal"]');
    if (!toggle || !windows.some(isDocked)) return;
    const selector = toggle.getAttribute('data-bs-target') || toggle.getAttribute('href');
    try { modalToggleTarget = selector ? document.querySelector(selector) : null; }
    catch { modalToggleTarget = null; }
    // A trusted click can run microtasks between its capture listeners.
    // Keep the target until the entire browser event has been dispatched.
    setTimeout(() => { modalToggleTarget = null; }, 0);
  }, true);

  document.addEventListener('show.bs.modal', event => {
    if (!event.defaultPrevented && event.target.classList.contains('modal')) {
      openModals.delete(event.target);
      openModals.add(event.target);
    }
  });

  document.addEventListener('shown.bs.modal', () => {
    if (usedDock) updateStacking();
  });

  windows.forEach(modal => {
    modal.addEventListener('hide.bs.modal', event => {
      if (isDocked(modal) && modalToggleTarget && modalToggleTarget !== modal) {
        event.preventDefault();
        return;
      }
      cancelAnimation(modal);
    });
    modal.addEventListener('hidden.bs.modal', () => {
      modal.classList.remove('chat-window-docked');
      instanceFor(modal)?._backdrop._element?.classList.remove('chat-window-backdrop');
      updateControls(modal);
    });
  });

  function handleModalHidden(event) {
    openModals.delete(event.target);
    if (!usedDock || (!windows.includes(event.target) && !windows.some(modal => openModals.has(modal)))) return;
    updateStacking();
    // Bootstrap releases the body lock and focus trap on every hide. Restore
    // them for any remaining blocking dialog, including after a dock is closed.
    const top = blockingModals().at(-1);
    if (top) {
      const instance = instanceFor(top);
      // A nested Bootstrap dialog may have restored an existing hidden
      // overflow value. Hiding again would save that as the unlocked value.
      if (document.body.style.overflow !== 'hidden') instance?._scrollBar.hide();
      document.body.classList.add('modal-open');
      document.querySelector('.body-header')?.style.setProperty('position', 'static', 'important');
      // Bootstrap removes document listeners when a different trap activates,
      // without clearing the previous trap's active flag.
      instance?._focustrap.deactivate();
      instance?._focustrap.activate();
    } else {
      document.body.classList.remove('modal-open');
      document.querySelector('.body-header')?.style.setProperty('position', 'sticky', 'important');
    }
  }

  document.addEventListener('hidden.bs.modal', handleModalHidden);
  document.addEventListener('lexius-modal-force-hidden', handleModalHidden);

  desktop.addEventListener('change', () => {
    if (!desktop.matches) windows.filter(isDocked).forEach(modal => setDocked(modal, false, false));
  });
  narrowDesktop.addEventListener('change', updateReadingLayout);
  window.addEventListener('lexius:document-rendered', updateReadingLayout);
}
